import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box as MuiBox,
  Grid,
  TextField as MuiTextField,
  Typography,
  CircularProgress,
  Alert
} from "@mui/material";
import styled from "@emotion/styled";
import RefreshIcon from "@mui/icons-material/Refresh";
import CopyToClipboardWrapper from "../../components/copyToClipboardWrapper/CopyToClipboardWrapper";

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

const Box = styled(MuiBox)`
  display: flex;
  gap: 28px;
  margin-top: 20px;
`;

const TextField = styled(MuiTextField)`
  display: flex;
  padding-top: 1px;
  justify-content: center;
  align-items: center label {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 0.15px;
  }
  label.additional-class {
    position: none !important;
    left: 0 !important;
    top: 0 !important;
  }
  additional-class-input {
    padding: 24px 0 5px !important;
  }

  input {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.15px;
    &::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
    &:hover:not(.Mui-disabled)::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
  }
`;

interface BusinessDetailsData {
  BusinessId: string;
  LegacyMerchantId: number;
  EstablishmentName: string;
  StreetNumber: string;
  Street: string;
  PostalCode: string;
  Suburb: string;
  State: string;
  InstagramHandle: string;
  PhoneNumber: string;
  WebAddress: string;
}

const DetailsTabPage = () => {
  const { businessId }: any = useParams();
  const [businessDetails, setBusinessDetails] =
    useState<BusinessDetailsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: ""
  });

  const fetchBusinessDetails = async () => {
    if (!businessId) return;

    try {
      setIsLoading(true);
      setNotificationMessage({
        message: "",
        type: ""
      });
      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios({
        method: "get",
        url: `${apiEndpoint}/datacollectionbusiness/support/business/${businessId}`,
        headers: {
          Authorization: `bearer ${accessToken}`,
          "Content-Type": "application/json"
        }
      });
      setBusinessDetails(response.data);
    } catch (err: any) {
      setNotificationMessage({
        type: "error",
        message: "Failed to fetch business details"
      });
      console.error("Failed to fetch business details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessDetails();
  }, [businessId]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!businessDetails) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          No business details available
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <div style={{ float: "right", cursor: "pointer" }}>
          <RefreshIcon
            onClick={() => {
              setNotificationMessage({
                message: "",
                type: ""
              });
              fetchBusinessDetails();
            }}
          />
        </div>

        {notificationMessage?.message && (
          <div style={{ marginBottom: "20px" }}>
            <Alert severity={notificationMessage?.type as any}>
              {notificationMessage?.message}
            </Alert>
          </div>
        )}

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-businessid-input"
            label="Business ID"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true,
              endAdornment: (
                <CopyToClipboardWrapper
                  text={businessDetails.BusinessId || ""}
                  cell="BusinessId"
                />
              )
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.BusinessId}
            fullWidth
            disabled={true}
          />

          <TextField
            id="standard-legacymerchantid-input"
            label="Legacy Merchant ID"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true,
              endAdornment: (
                <CopyToClipboardWrapper
                  text={businessDetails.LegacyMerchantId?.toString() || ""}
                  cell="LegacyMerchantId"
                />
              )
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            fullWidth
            value={businessDetails.LegacyMerchantId}
            disabled={true}
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-establishmentname-input"
            label="Establishment Name"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.EstablishmentName}
            fullWidth
            disabled={true}
          />
          <TextField
            id="standard-streetnumber-input"
            label="Street Number"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.StreetNumber}
            fullWidth
            disabled={true}
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-street-input"
            label="Street"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.Street}
            fullWidth
            disabled={true}
          />
          <TextField
            id="standard-postalcode-input"
            label="Postal Code"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.PostalCode}
            fullWidth
            disabled={true}
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-suburb-input"
            label="Suburb"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.Suburb}
            fullWidth
            disabled={true}
          />

          <TextField
            id="standard-state-input"
            label="State"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.State}
            fullWidth
            disabled={true}
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-instagramhandle-input"
            label="Instagram Handle"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.InstagramHandle}
            fullWidth
            disabled={true}
          />

          <TextField
            id="standard-phonenumber-input"
            label="Phone Number"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.PhoneNumber}
            fullWidth
            disabled={true}
          />
        </Box>

        <Box display="flex" flexDirection="row" gap={3}>
          <TextField
            id="standard-webaddress-input"
            label="Web Address"
            InputLabelProps={{ className: "additional-class" }}
            InputProps={{
              className: "additional-class-input",
              readOnly: true
            }}
            type="text"
            variant="standard"
            sx={{ height: "55px" }}
            value={businessDetails.WebAddress}
            fullWidth
            disabled={true}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default DetailsTabPage;
